import React, {useEffect, useState} from 'react'
import {Box, Spinner, Text} from 'grommet'
import {ReactComponent as ArrowDownLongImg} from '../../assets/arrow_down_long.svg'
import styled from "styled-components";
import {erc20ABI, useAccount, useBalance, useContractWrite, useWaitForTransaction} from "wagmi";
import config from "../../config";
import wethABI from "../../abi/weth.json";
import {GradientFilledButton} from "../../components/button";
import {SwapSide} from './SwapSide'
import {TokensList, WrappedNativeToken} from "../../tokens";
import {BaseTradeConfig, generateRoute} from "../../api/uniswap";
import {ButtonProps} from "antd";
import useDebounce from "../../hooks/useDebounce";
import {inputAmount} from "../../utils/number";
import {Currency} from "@uniswap/sdk-core/dist/entities/currency";
import {FeeAmount} from "@uniswap/v3-sdk";
import { useSendTransaction } from 'wagmi'
import {toast} from "react-toastify";
import {getAllowance} from "../../api/erc20";
import {fromReadableAmount} from "../../utils";

export type SwapSideType = 'pay' | 'receive'

const LKKeyToken = 'harmony_portfolio_token_id';

const saveSelectedToken = (side: SwapSideType, value: string) => {
  window.localStorage.setItem(`${LKKeyToken}_${side}`, value)
}

const getSelectedToken = (side: SwapSideType) => {
  return window.localStorage.getItem(`${LKKeyToken}_${side}`)
}

const defaultPayState: SwapSideState = {
  amount: '0',
  token: TokensList.find(token => token.symbol === getSelectedToken('pay')),
  balance: ''
}

const defaultReceiveState: SwapSideState = {
  amount: '0',
  token: TokensList.find(token => token.symbol === getSelectedToken('receive')),
  balance: ''
}

export interface SwapSideState {
  amount: string
  token?: Currency
  balance: string
}

const SwapButton = (props: {
  text: string;
  disabled?: boolean;
  loading?: boolean
  onClick: () => void
}) => {
  const { disabled = false, loading = false, onClick } = props

  const buttonProps: ButtonProps = {
    disabled,
    style: {
      height: '60px',
      opacity: (loading || disabled) ? 0.4 : 1
    }
  }

  return <GradientFilledButton {...buttonProps} onClick={onClick}>
    <Box direction={'row'} align={'center'} justify={'center'} gap={'8px'}>
      {loading &&
          <Spinner color={'white'} />
      }
      <Text color={'#323232'} size={'30px'} weight={500}>
        {props.text}
      </Text>
    </Box>
  </GradientFilledButton>
}

const SideSwitch = styled(Box)`
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    background: #5F5F5F;
    border: 4px solid #323232;
    border-radius: 15px;
    padding: 14px 17px;
`

export const SwapWidget = () => {
  const { address: userAddress, isConnected } = useAccount()
  const [statePay, setStatePay] = useState<SwapSideState>(defaultPayState)
  const [stateReceive, setStateReceive] = useState<SwapSideState>(defaultReceiveState)
  const payAmount = useDebounce(statePay.amount, 300)
  const receiveAmount = useDebounce(stateReceive.amount, 300)
  const [isTradeQuoteLoading, setTradeQuoteLoading] = useState(false)
  const { data: swapTxResult, isLoading: isSwapTxLoading, sendTransactionAsync, reset: resetSendTransaction } = useSendTransaction()
  const { data: swapTxReceipt, isFetching: isSwapConfirming } = useWaitForTransaction({
    hash: swapTxResult?.hash,
  })

  const payBalance = useBalance({
    address: userAddress,
    enabled: isConnected,
    token: statePay.token?.isToken ? statePay.token.address as `0x${string}` : undefined,
    watch: true,
  })

  const receiveBalance = useBalance({
    address: userAddress,
    enabled: isConnected,
    token: stateReceive.token?.isToken ? stateReceive.token.address as `0x${string}` : undefined,
    watch: true,
  })

  const isSufficientBalance = +(payBalance.data?.formatted || '0') > +statePay.amount

  const {
    isLoading: wrapIsLoading,
    writeAsync: wrapAsync,
  } = useContractWrite({
    address: config.wrappedOneContractAddress as `0x${string}`,
    abi: wethABI,
    functionName: 'deposit',
  })

  const {
    isLoading: unwrapIsLoading,
    writeAsync: unwrapAsync,
  } = useContractWrite({
    address: config.wrappedOneContractAddress as `0x${string}`,
    abi: wethABI,
    functionName: 'withdraw',
  })

  const { writeAsync: approveAsync } = useContractWrite({
    address: '0x',
    abi: erc20ABI,
    functionName: 'approve',
    args: ['0x', 0n],
  })

  const loadQuoteData = async () => {
    let newAmount = '0'
    setTradeQuoteLoading(true)
    try {
      const tradeConfig = getTradeConfig()
      if(tradeConfig && +tradeConfig.tokens.amountIn > 0) {
        const route = await generateRoute(tradeConfig)
        console.log('Quote route:', route)
        if(route) {
          newAmount = route.quote.toExact()
        }
      }
    } catch (e) {
      console.error('quote error:', e)
    } finally {
      setTradeQuoteLoading(false)
      setStateReceive(currentState => {
        return {
          ...currentState,
          amount: newAmount
        }
      })
    }
  }

  useEffect(() => {
    if(!isSufficientBalance) {
      // return
    }
    if(statePay.token && stateReceive.token) {
      if(statePay.token.isNative && stateReceive.token.equals(WrappedNativeToken)) {
        setStateReceive((currentState) => {
          return {
            ...currentState,
            amount: statePay.amount
          }
        })
      } else if(stateReceive.token.isNative && statePay.token.equals(WrappedNativeToken)) {
        setStateReceive((currentState) => {
          return {
            ...currentState,
            amount: statePay.amount
          }
        })
      } else {
        loadQuoteData()
      }
    }
  }, [statePay.token, payAmount, isSufficientBalance]);

  useEffect(() => {
    if(payBalance.data) {
      setStatePay(currentState => {
        return {
          ...currentState,
          balance: payBalance.data ? payBalance.data.formatted : ''
        }
      })
    }
  }, [payBalance.data]);

  useEffect(() => {
    if(receiveBalance.data) {
      setStateReceive(currentState => {
        return {
          ...currentState,
          balance: receiveBalance.data ? receiveBalance.data.formatted : ''
        }
      })
    }
  }, [receiveBalance.data]);

  useEffect(() => {
    if(swapTxReceipt && swapTxReceipt.transactionHash) {
      payBalance.refetch()
      receiveBalance.refetch()

      toast.success(<Box direction={'row'}>
        <Text>Swap successful!</Text>
      </Box>, {
        toastId: swapTxReceipt.transactionHash
      })
      setStatePay(currentState => ({...currentState, amount: '0'}))
      setStateReceive(currentState => ({...currentState, amount: '0'}))
    }
  }, [swapTxReceipt])

  const onPayAmountChanged = (amount: string) => {
    setStatePay(currentState => {
      return {
        ...currentState,
        amount
      }
    })
  }

  const onReceiveAmountChanged = (amount: string) => {
    setStateReceive(currentState => {
      return {
        ...currentState,
        amount
      }
    })
  }

  const onPayTokenChange = (token: Currency) => {
    setStatePay(currentState => {
      return {
        ...currentState,
        amount: '0',
        token
      }
    })
    saveSelectedToken('pay', token.symbol as string)
  }

  const onReceiveTokenChange = (token: Currency) => {
    setStateReceive(currentState => {
      return {
        ...currentState,
        amount: '0',
        token
      }
    })
    saveSelectedToken('receive', token.symbol as string)
  }

  const onWrapClicked = async () => {
    try {
      if(statePay.token) {
        const amount = inputAmount(statePay.amount, statePay.token)
        const amountRaw = BigInt(amount ? amount.multiply(10**statePay.token.decimals).toExact() : '0')
        const data = await wrapAsync({
          value: amountRaw
        })
        console.log('Deposit result', data)
      }
    } catch (e) {
      console.error('Failed wrap:', e)
    }
  }

  const onUnwrapClicked = async () => {
    try {
      if(statePay.token) {
        const amount = inputAmount(statePay.amount, statePay.token)
        const amountRaw = BigInt(amount ? amount.multiply(10**statePay.token.decimals).toExact() : '0')
        const data = await unwrapAsync({
          args: [amountRaw]
        })
        console.log('Withdraw result', data)
      }
    } catch (e) {
      console.error('Failed wrap:', e)
    }
  }

  const getTradeConfig = (): BaseTradeConfig | null => {
    if(statePay.token && stateReceive.token) {
      return {
        wallet: {
          address: userAddress as string
        },
        tokens: {
          in: statePay.token,
          // amountIn: 0.3,
          amountIn: +statePay.amount || 0,
          out: stateReceive.token,
          poolFee: FeeAmount.MEDIUM,
        },
      };
    }
    return null
  }

  const onSwapClicked = async () => {
    try {
      if(!statePay.token || !stateReceive.token || !userAddress) {
        return
      }

      if(!statePay.token.isNative) {
        const amountBN = fromReadableAmount(+statePay.amount, statePay.token.decimals)
        const amount = BigInt(amountBN.toString())
        const allowance = await getAllowance(statePay.token.address, userAddress, config.universalRouterAddress)
        console.log('Allowance:', allowance, 'amount:', amount)

        if(allowance < amount) {
          await approveAsync({
            // @ts-ignore
            address: statePay.token.address as `0x${string}`,
            args: [config.universalRouterAddress as `0x${string}`, amount]
          })
          await new Promise(resolve => setTimeout(resolve, 5000))
        }
      }

      const tradeConfig = getTradeConfig()
      if(tradeConfig) {
        setTradeQuoteLoading(true)
        const route = await generateRoute(tradeConfig)
        console.log('Trade route:', route)
        if(route && route.methodParameters) {
          const res = await sendTransactionAsync({
            data: route.methodParameters.calldata as `0x${string}`,
            to: config.universalRouterAddress,
            value: BigInt(route.methodParameters.value),
          })
          console.log('Swap tx sent:', res.hash)
        }
      }
    } catch (e) {
      console.log("Failed to swap tokens:", e)
    } finally {
      setTradeQuoteLoading(false)
    }
  }

  const formHasAmounts = +statePay.amount > 0 && +stateReceive.amount > 0
  const isDisabled = !formHasAmounts || wrapIsLoading || unwrapIsLoading || !isSufficientBalance
  const isLoading = isTradeQuoteLoading || isSwapConfirming || wrapIsLoading || unwrapIsLoading || isSwapTxLoading

  return <Box
    background={'#323232'}
    pad={'24px'}
    round={'28px'}
  >
    <Box gap={'4px'} style={{ position: 'relative' }}>
      <SwapSide
        type={'pay'}
        data={statePay}
        onChangeAmount={onPayAmountChanged}
        onChangeToken={onPayTokenChange}
      />
      <SwapSide
        type={'receive'}
        data={stateReceive}
        onChangeAmount={onReceiveAmountChanged}
        onChangeToken={onReceiveTokenChange}
      />
      <SideSwitch onClick={() => {
        setStatePay(stateReceive)
        setStateReceive(statePay)
      }}>
        <ArrowDownLongImg />
      </SideSwitch>
    </Box>
    <Box margin={{ top: '40px' }}>
      {
        (statePay.token?.isNative && stateReceive.token?.symbol === 'WONE')
        ? <SwapButton text={'Wrap'} disabled={isDisabled} loading={isLoading} onClick={onWrapClicked} />
        : (statePay.token?.symbol === 'WONE' && stateReceive.token?.isNative) ?
          <SwapButton text={'Unwrap'} disabled={isDisabled} loading={isLoading} onClick={onUnwrapClicked} />
          :
          <SwapButton
            text={!isSufficientBalance ? `Insufficient ${statePay.token?.symbol} balance` : isSwapConfirming ? 'Executing swap...' : 'Swap'}
            disabled={isDisabled}
            loading={isLoading}
            onClick={onSwapClicked}
          />
      }
    </Box>
  </Box>
}
